import { SHStack } from "@components/design-systems";
import {
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { memo, useMemo } from "react";
import { SummaryAnalysisTableBody } from "../body";
import { SummaryAnalysisTableFooter } from "../footer";
import { SummaryAnalysisTableHead } from "../head";
import { SummaryAnalysisTableWrapper } from "../wrapper";

interface SummaryAnalysisTableProps {
  isReview?: boolean;
  rows: FeatureAnalysisRowDTO[];
  selectedProducts: SelectedProductDTO[];
  removeFeatureLevelRows?: boolean;
  removeSubSectionLevelRows?: boolean;
  wrapperProps?: React.ComponentProps<typeof SummaryAnalysisTableWrapper>;
  productOrders?: Record<string, number>;
}

export const SummaryAnalysisTable = memo(
  ({
    isReview,
    rows,
    selectedProducts,
    wrapperProps,
    removeFeatureLevelRows = wrapperProps?.viewMode !== TableViewMode.Detail,
    removeSubSectionLevelRows = wrapperProps?.viewMode ===
      TableViewMode.Summary,
  }: SummaryAnalysisTableProps) => {
    const containsMissingEssential = useMemo(
      () => selectedProducts?.some((product) => product.isMissingEssential),
      [selectedProducts],
    );
    const containsEssentialFeature = useMemo(
      () =>
        rows?.some((row) =>
          row.subRows?.some((subRow) =>
            subRow.subRows?.some((feature) => feature.isEssential),
          ),
        ),
      [rows],
    );

    return (
      <SHStack width={"100%"} sx={{ overflowX: "auto", overflowY: "hidden" }}>
        <SummaryAnalysisTableWrapper hideArrow {...wrapperProps}>
          <SummaryAnalysisTableHead
            isReview={isReview}
            selectedProducts={selectedProducts}
          />

          <SummaryAnalysisTableBody
            rows={rows}
            removeFeatureLevelRows={removeFeatureLevelRows}
            removeSubSectionLevelRows={removeSubSectionLevelRows}
          />

          <SummaryAnalysisTableFooter
            containsEssentialFeature={containsEssentialFeature}
            containsMissingEssential={containsMissingEssential}
          />
        </SummaryAnalysisTableWrapper>
      </SHStack>
    );
  },
);
